/*
 * Size Guide Drawer
 * Hooks: [data-size-guide], [data-size-guide-drawer], [data-size-guide-overlay],
 *        [data-size-guide-close], [data-size-guide-open], [data-size-guide-unit]
 * Depends on: focus-trap.js (loaded globally in theme.liquid)
 */

(function () {
  'use strict';

  /* global createFocusTrap */

  var container = document.querySelector('[data-size-guide]');
  if (!container) return;

  var drawer     = container.querySelector('[data-size-guide-drawer]');
  var overlay    = container.querySelector('[data-size-guide-overlay]');
  var contentEl  = drawer ? drawer.querySelector('[data-size-guide-content]') : null;
  var slideTimer = null;
  var lastTrigger = null;

  var trap = typeof createFocusTrap === 'function' ? createFocusTrap(drawer) : null;

  // --- Open / Close -------------------------------------------------------

  // inert only, never aria-hidden. On close, remove inert BEFORE restoring focus
  // to the trigger, or focus() lands inside an inert <main> and silently no-ops.
  function setBackgroundInert(on) {
    // Header excluded — its icons/toggle are drawer chrome (see drawer-coordinator.js).
    ['[data-announcement-bar]', 'main', '[data-footer]'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el) el.toggleAttribute('inert', on);
    });
  }

  var tablesBuilt = false;

  function openDrawer(trigger) {
    if (slideTimer) { clearTimeout(slideTimer); slideTimer = null; }
    lastTrigger = trigger || null;
    container.setAttribute('data-size-guide-open', 'true');
    if (!tablesBuilt) {
      tablesBuilt = true;
      container.querySelectorAll('[data-size-guide-data-col]').forEach(buildTableScrollbar);
    }
    const sc = container.querySelector('[data-drawer-scroll]'); if (sc) sc.scrollTop = 0; // open from the top
    setUnit('in');
    drawer.removeAttribute('aria-hidden');
    drawer.removeAttribute('inert');
    document.documentElement.setAttribute('data-scroll-lock', '');
    setBackgroundInert(true);
    if (trap) { trap.activate(); trap.focusFirst(); }
    slideTimer = setTimeout(function () {
      slideTimer = null;
      if (contentEl) contentEl.classList.remove('opacity-0');
    }, 300);
  }

  function closeDrawer() {
    if (slideTimer) { clearTimeout(slideTimer); slideTimer = null; }
    if (contentEl) contentEl.classList.add('opacity-0');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    document.documentElement.removeAttribute('data-scroll-lock');
    setBackgroundInert(false);
    if (trap) trap.deactivate();
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
    slideTimer = setTimeout(function () {
      slideTimer = null;
      container.removeAttribute('data-size-guide-open');
    }, 300);
  }

  // --- Unit toggle --------------------------------------------------------

  function setUnit(unit) {
    if (unit !== 'in' && unit !== 'cm') return;
    container.setAttribute('data-unit', unit);
    container.querySelectorAll('[data-size-guide-unit]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.sizeGuideUnit === unit ? 'true' : 'false');
    });
  }

  // --- Event delegation ---------------------------------------------------

  // Trigger: any [data-size-guide-open] anywhere on the page (excluding the container itself)
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-size-guide-open]');
    if (trigger && trigger !== container) { e.preventDefault(); openDrawer(trigger); return; }

    var closeBtn = e.target.closest('[data-size-guide-close]');
    if (closeBtn) { closeDrawer(); return; }

    var unitBtn = e.target.closest('[data-size-guide-unit]');
    if (unitBtn && container.hasAttribute('data-size-guide-open')) {
      setUnit(unitBtn.dataset.sizeGuideUnit);
      return;
    }
  });

  // Overlay click — gated so a double-close cannot occur if this fires after delegated handler
  if (overlay) {
    overlay.addEventListener('click', function () {
      if (container.hasAttribute('data-size-guide-open')) closeDrawer();
    });
  }

  // Escape key
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && container.hasAttribute('data-size-guide-open')) {
      closeDrawer();
    }
  });

  // --- Size tables: wheel + persistent horizontal scrollbar ---------------
  // The platform bar only shows mid-gesture, so an overflowing table reads as a static one. Drawn here
  // because drawer-scrollbar.js is vertical-only; a sibling, so it never inflates scrollWidth.

  var drawerBody = drawer ? drawer.querySelector('[data-drawer-scroll]') : null;

  function styled(css) {
    var el = document.createElement('div');
    el.style.cssText = css;
    return el;
  }

  function buildTableScrollbar(el) {
    // Grab zone matches the vertical bar, laid over the table's last row.
    var bar = styled('position:absolute;z-index:20;height:var(--spacing-xl);touch-action:none;');
    bar.setAttribute('aria-hidden', 'true');
    bar.appendChild(styled('position:absolute;left:0;right:0;bottom:0;height:var(--spacing-2xs);background:var(--color-rule-tertiary);'));
    var thumb = bar.appendChild(styled('position:absolute;bottom:0;height:var(--spacing-2xs);background:var(--color-purple);'));
    el.insertAdjacentElement('afterend', bar);

    // Focusable so arrow keys reach it on a click or Tab.
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    if (getComputedStyle(el.parentNode).position === 'static') el.parentNode.style.position = 'relative';

    function update() {
      var cw = el.clientWidth;
      if (el.scrollWidth - cw <= 1) { bar.style.display = 'none'; return; }
      // The row-label column is sticky: the bar spans and measures only what moves.
      var th = el.querySelector('th');
      var f = th && getComputedStyle(th).position === 'sticky' ? th.getBoundingClientRect().width : 0;
      var scrollable = el.scrollWidth - f;
      bar.style.display = 'block';
      bar.style.left = (el.offsetLeft + f) + 'px';
      bar.style.width = (cw - f) + 'px';
      bar.style.top = (el.offsetTop + el.offsetHeight - bar.offsetHeight) + 'px';
      thumb.style.width = ((cw - f) / scrollable * 100) + '%';
      thumb.style.left = (el.scrollLeft / scrollable * 100) + '%';
    }

    // Pointer maps to the thumb's middle, so the grab point tracks it.
    function drag(e) {
      var rect = bar.getBoundingClientRect();
      if (!rect.width) return;
      var max = el.scrollWidth - el.clientWidth;
      var ratio = (e.clientX - rect.left) / rect.width - (el.clientWidth / el.scrollWidth) / 2;
      el.scrollLeft = Math.max(0, Math.min(max, ratio * el.scrollWidth));
      e.preventDefault();
    }
    bar.addEventListener('pointerdown', function (e) {
      bar.setPointerCapture(e.pointerId);
      // Same grab cursors as the vertical bar: grabbing page-wide while dragging (unlayered CSS).
      document.documentElement.setAttribute('data-drawer-dragging', '');
      bar.style.cursor = 'grabbing';
      drag(e);
    });
    bar.addEventListener('pointermove', function (e) { if (bar.hasPointerCapture(e.pointerId)) drag(e); });
    bar.addEventListener('lostpointercapture', function () {
      document.documentElement.removeAttribute('data-drawer-dragging');
      bar.style.cursor = '';
    });
    // Grab over the thumb, default over the rest of the band.
    bar.addEventListener('mousemove', function (e) {
      if (document.documentElement.hasAttribute('data-drawer-dragging')) return;
      var tr = thumb.getBoundingClientRect();
      bar.style.cursor = (e.clientX >= tr.left && e.clientX <= tr.right) ? 'grab' : '';
    });

    // Sideways gestures move the columns natively. Upright ones drive the drawer from here, or a trackpad
    // swipe's stray horizontal component drifts them. A mouse uses the bar instead.
    el.addEventListener('wheel', function (e) {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) || !drawerBody) return;
      e.preventDefault();
      drawerBody.scrollTop += e.deltaY;
    }, { passive: false });

    el.addEventListener('scroll', update, { passive: true });
    // The unit toggle changes the table's width, a resize the container's — both move the thumb.
    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(update);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    update();
  }

  // An accordion panel grows after reporting open without resizing the body — the change the drawer's
  // vertical bar misses. A scroll event is the signal it acts on.
  if (drawerBody && typeof ResizeObserver === 'function') {
    var panels = new ResizeObserver(function () { drawerBody.dispatchEvent(new Event('scroll')); });
    drawer.querySelectorAll('[data-accordion-panel]').forEach(function (p) { panels.observe(p); });
  }

})();
